import { Component, DestroyRef, OnInit, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { TranslateModule } from '@ngx-translate/core'
import { DialogModule } from 'primeng/dialog'
import { PassUploadJobsService, PassUploadDefect, PassUploadDefectsResponse } from '@app/shared/services/pass-upload-jobs.service'
import { UploadTasksService } from '@app/shared/services/upload-tasks.service'
import { PassUploadJob, TypedPassUploadKey, passUploadTextKey } from '@app/models/pass-upload-job.model'

/**
 * Upload-errors dialog (YB-1669), mounted ONCE in the app root.
 *
 * Opens when an upload had invalid rows (UploadTasksService.errorJob$ — the valid rows
 * are already queued and committing in the background). Shows up to 50 errored rows and
 * lets the user download the error xlsx (the uploaded file + an `errors` column) so they
 * can fix those rows and re-upload JUST them.
 *
 * Also re-openable later from the bell (uploadTasks.openErrors) — the defects are parsed
 * on demand from the stored error file, which outlives the run, so a user who missed the
 * dialog the first time can still get the list back.
 */
@Component({
  selector: 'app-pass-upload-errors',
  standalone: true,
  imports: [TranslateModule, DialogModule],
  template: `
    <p-dialog
      [(visible)]="visible"
      [modal]="true"
      [style]="{ width: '760px', maxWidth: '95vw' }"
      [draggable]="false"
      [resizable]="false"
      [header]="(job ? text('ERRORS_TITLE') : 'PASS_UPLOADS.ERRORS_TITLE') | translate"
    >
      @if (job) {
        <div>
          <div class="pu-summary">
            <span>{{ 'PASS_UPLOADS.TOTAL_ROWS' | translate }}: <b>{{ job.total_rows }}</b></span>
            <span class="color-success">{{ text('VALID_ROWS') | translate }}: <b>{{ job.valid_rows }}</b></span>
            <span class="color-danger">{{ text('INVALID_ROWS') | translate }}: <b>{{ job.invalid_rows }}</b></span>
          </div>
          <p class="pu-note">{{ text(uploadTasks.isActive(job) ? 'ERRORS_NOTE' : 'ERRORS_NOTE_DONE') | translate }}</p>

          @if (defects.length) {
            <div class="pu-errors">
              <table class="pu-table">
                <thead>
                  <tr>
                    <th>{{ 'PASS_UPLOADS.ROW' | translate }}</th>
                    <th>{{ 'PASS_UPLOADS.IDENTITY' | translate }}</th>
                    <th>{{ 'PASS_UPLOADS.REASON' | translate }}</th>
                  </tr>
                </thead>
                <tbody>
                  @for (defect of defects; track defect.row) {
                    <tr>
                      <td>{{ defect.row }}</td>
                      <td>{{ defect.identity }}</td>
                      <td class="pu-reason">{{ defect.reason }}</td>
                    </tr>
                  }
                </tbody>
              </table>
              @if (totalDefects > defects.length) {
                <p class="pu-note">
                  {{ 'PASS_UPLOADS.ERRORS_PARTIAL' | translate: { shown: defects.length, total: totalDefects } }}
                </p>
              }
            </div>
          }

          <div class="pu-actions">
            <button class="btn btn-primary" (click)="downloadErrorFile()">{{ 'PASS_UPLOADS.DOWNLOAD_ERRORS' | translate }}</button>
            <button class="btn" (click)="visible = false">{{ 'PASS_UPLOADS.CLOSE' | translate }}</button>
          </div>
        </div>
      }
    </p-dialog>
  `,
  styles: [
    `
      .pu-summary { display: flex; gap: 1.5rem; margin-bottom: 0.75rem; }
      .pu-note { margin: 0.5rem 0; font-size: 0.9rem; }
      .pu-errors { max-height: 320px; overflow: auto; border: 1px solid var(--surface-border, #ddd); border-radius: 4px; }
      .pu-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
      .pu-table th, .pu-table td { padding: 0.35rem 0.5rem; border-bottom: 1px solid var(--surface-border, #eee); text-align: start; white-space: nowrap; }
      .pu-table td.pu-reason { white-space: normal; }
      .pu-actions { display: flex; gap: 0.75rem; margin-top: 1rem; justify-content: flex-end; }
    `,
  ],
})
export class PassUploadErrorsComponent implements OnInit {
  private destroyRef = inject(DestroyRef)

  visible = false
  job: PassUploadJob | null = null
  defects: PassUploadDefect[] = []
  totalDefects = 0

  private readonly DEFECTS_LIMIT = 50

  constructor(
    private passUploadJobs: PassUploadJobsService,
    public uploadTasks: UploadTasksService,
  ) {}

  ngOnInit(): void {
    this.uploadTasks.errorJob$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((job) => this.show(job))
  }

  show(job: PassUploadJob) {
    this.job = job
    this.defects = []
    this.totalDefects = job.invalid_rows
    this.visible = true
    this.passUploadJobs
      .getDefects(+job.id, this.DEFECTS_LIMIT)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: PassUploadDefectsResponse) => {
        if (!response?.success) return
        this.defects = response.defects ?? []
        this.totalDefects = response.total ?? this.defects.length
      })
  }

  /** The key for a sentence whose wording depends on what this job did — see passUploadTextKey. */
  text(key: TypedPassUploadKey): string {
    return passUploadTextKey(this.job?.job_type, key)
  }

  downloadErrorFile() {
    if (this.job) this.uploadTasks.downloadErrorFile(+this.job.id)
  }
}
